// src/catalog/components/detail/detailUtils.js

import {
  formatLabel,
  formatLanguage
} from "../../utils/catalogDisplay";

export function getRelatedEntryLabel(relatedEntry) {
  return (
    relatedEntry.label ||
    formatLabel(relatedEntry.slug) ||
    "Related entry"
  );
}

export function getRelationLabel(relation) {
  if (!relation) return "Related";

  const labels = {
    participantEntryPoint: "Participant entry point",
    moderatorTool: "Moderator tool",
    relatedActivity: "Related activity",
    prerequisite: "Prerequisite",
    followUp: "Follow-up",
    alternative: "Alternative"
  };

  return labels[relation] || formatLabel(relation);
}

export function getRequiredAccessContexts(accessRequirements) {
  if (!accessRequirements) return [];

  const contexts = [];

  if (
    Array.isArray(accessRequirements.acceptedEntitlementTypes) &&
    accessRequirements.acceptedEntitlementTypes.length > 0
  ) {
    contexts.push(
      `Accepted entitlements: ${accessRequirements.acceptedEntitlementTypes
        .map(formatLabel)
        .join(" or ")}`
    );
  }

  if (accessRequirements.requiresInstitutionAssignment) {
    contexts.push("Institution assignment");
  }

  if (accessRequirements.requiresProjectContext) {
    contexts.push("Project context");
  }

  if (accessRequirements.requiresProgramContext) {
    contexts.push("Program context");
  }

  if (accessRequirements.requiresCohortContext) {
    contexts.push("Cohort context");
  }

  if (accessRequirements.requiresSessionContext) {
    contexts.push("Session context");
  }

  if (accessRequirements.requiresSessionCode) {
    contexts.push("Session code");
  }

  return contexts;
}

export function hasInstrumentMetadata(instrumentMetadata) {
  if (!instrumentMetadata) return false;

  return Boolean(
    instrumentMetadata.baseInstrument ||
      instrumentMetadata.instrumentFamily ||
      instrumentMetadata.adaptationLabel ||
      instrumentMetadata.adaptationLanguage ||
      instrumentMetadata.adaptationLocale ||
      instrumentMetadata.adaptationStatus
  );
}

export function getInstrumentAttributeLabels(instrumentMetadata) {
  if (!Array.isArray(instrumentMetadata?.attributes)) {
    return [];
  }

  return instrumentMetadata.attributes
    .map((attribute) =>
      String(attribute?.label || "").trim()
    )
    .filter(Boolean);
}

export function getInstrumentRatingScaleValues(instrumentMetadata) {
  const values = instrumentMetadata?.responseScale?.values;

  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter(
    (value) => value !== null && value !== undefined && value !== ""
  );
}

export function hasMethodMetadata(methodMetadata) {
  if (!methodMetadata || typeof methodMetadata !== "object") {
    return false;
  }

  return Boolean(
    String(methodMetadata.label || "").trim() ||
      String(methodMetadata.description || "").trim()
  );
}

export function getUserFacingAccessSummary(entry) {
  const accessModel = entry?.accessModel;
  const participationMode = entry?.participationMode;

  if (
    accessModel === "freeWithLogin" &&
    participationMode === "loginRequired"
  ) {
    return "Free with an All-Voiced account.";
  }

  if (
    accessModel === "free" &&
    participationMode === "publicExploreOnly"
  ) {
    return "Available without an All-Voiced account.";
  }

  if (accessModel === "institutionalLicense") {
    return "Available through an institutional All-Voiced deployment.";
  }

  if (accessModel === "customQuote") {
    return "Available through a tailored All-Voiced engagement.";
  }

  if (accessModel === "free") {
    return "Available without additional payment.";
  }

  return formatLabel(accessModel);
}

export function getCompactOverviewAccessLabel(entry) {
  const accessModel = entry?.accessModel;
  const participationMode = entry?.participationMode;

  if (
    accessModel === "freeWithLogin" &&
    participationMode === "loginRequired"
  ) {
    return "Free";
  }

  if (
    accessModel === "free" &&
    participationMode === "publicExploreOnly"
  ) {
    return "No account required";
  }

  return getUserFacingAccessSummary(entry);
}

export function isSimpleAccessEntry(entry) {
  const summary = getUserFacingAccessSummary(entry);
  const requiredAccessContexts = getRequiredAccessContexts(
    entry?.accessRequirements
  );

  if (!summary) {
    return false;
  }

  if (requiredAccessContexts.length > 0) {
    return false;
  }

  return !["institutionalLicense", "customQuote"].includes(
    entry?.accessModel
  );
}

export function getVoiceSampleLanguageLabels(
  entry,
  selectedLaunchConfiguration = null
) {
  const configurationVoiceLanguages = Array.isArray(
    selectedLaunchConfiguration?.voiceLanguages
  )
    ? selectedLaunchConfiguration.voiceLanguages
    : [];
  const resourceVoiceLanguages = Array.isArray(
    entry?.metadata?.content?.voiceLanguages
  )
    ? entry.metadata.content.voiceLanguages
    : [];
  const effectiveVoiceLanguages =
    configurationVoiceLanguages.length > 0
      ? configurationVoiceLanguages
      : resourceVoiceLanguages;

  if (effectiveVoiceLanguages.length === 0) {
    return [];
  }

  if (
    configurationVoiceLanguages.length === 0 &&
    effectiveVoiceLanguages.length === 1 &&
    String(effectiveVoiceLanguages[0]).trim().toLowerCase() === "any"
  ) {
    return [];
  }

  const seen = new Set();

  return effectiveVoiceLanguages
    .map((value) => {
      const normalized = String(value || "").trim().toLowerCase();

      if (!normalized) {
        return null;
      }

      if (normalized === "any") {
        return "Any language";
      }

      return formatLanguage(value);
    })
    .filter((value) => {
      if (!value || seen.has(value)) {
        return false;
      }

      seen.add(value);
      return true;
    });
}
